import { z } from "zod";

export const BUG_SCREENSHOT_MAX_BYTES = 2 * 1024 * 1024;
export const BUG_STATUSES = ["new", "investigating", "fixed", "closed"] as const;
export type BugStatus = typeof BUG_STATUSES[number];
export const bugStatusLabel: Record<BugStatus, string> = {
  new: "New", investigating: "Investigating", fixed: "Fixed", closed: "Closed",
};

/** Keep useful navigation state without copying search text, tokens or fragments. */
export function bugPagePath(value: string): string {
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return "/";
  try {
    const url = new URL(value, "https://hub.invalid");
    if (url.origin !== "https://hub.invalid") return "/";
    const query = new URLSearchParams();
    for (const key of ["student", "queue", "tab", "view", "writeup", "date", "from", "to", "page"]) {
      const item = url.searchParams.get(key);
      if (item && /^[a-zA-Z0-9_-]{1,100}$/.test(item)) query.set(key, item);
    }
    return url.pathname + (query.size ? `?${query}` : "");
  } catch { return "/"; }
}

const dimension = z.number().int().min(1).max(20000);
export const bugReportInput = z.object({
  submissionId: z.string().uuid(),
  pagePath: z.string().max(2048).transform(bugPagePath),
  pageTitle: z.string().trim().max(200),
  description: z.string().trim().min(10).max(5000),
  expected: z.string().trim().max(2000).default(""),
  studentId: z.string().uuid().nullable().default(null),
  client: z.object({
    viewportWidth: dimension, viewportHeight: dimension,
    screenWidth: dimension, screenHeight: dimension,
    pixelRatio: z.number().min(0.1).max(10),
    language: z.string().max(50), timezone: z.string().max(100),
    online: z.boolean(),
    buildAsset: z.string().max(200).regex(/^(?:\/assets\/[a-zA-Z0-9_.-]+\.js)?$/),
  }).strict(),
}).strict();
export type BugReportInput = z.infer<typeof bugReportInput>;
export function bugTicketNumber(number: string | number): string {
  return `BUG-${String(number).padStart(6, "0")}`;
}
export interface BugSubmissionResult { id: string; ticketNumber: string; awardEarned: boolean }
export interface BugTicketSummary {
  id: string; ticketNumber: string; status: BugStatus; pagePath: string; pageTitle: string;
  reporter: string; reporterRoles: string[]; studentName: string | null;
  createdAt: string; updatedAt: string; hasScreenshot: boolean; confirmedAt: string | null;
}
export interface BugTicket extends BugTicketSummary {
  description: string; expected: string; client: BugReportInput["client"];
  userAgent: string; reporterUserId: string; studentId: string | null;
  staffNotes: string; version: number; screenshotUrl: string | null;
  confirmedBy: string | null; studentSubmitted: boolean; awardsEnabled: boolean;
}
export interface BugTicketList { items: BugTicketSummary[]; total: number; page: number; totalPages: number }
export type BugAwardState = "released" | "already-earned" | "pending-launch" | "not-eligible" | "withheld";
export interface BugConfirmationResult { ticket: BugTicket; awardState: BugAwardState }
